/**
 * /subscriptions — Customer's subscription overview (OCP-115).
 *
 * Lists every subscription this customer has (active + cancelled),
 * one card per row. Active rows surface period-end + cancel-pending
 * badge, cancelled rows are dimmed. Each card links to
 * `/subscriptions/:id` for the manage flow.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../lib/api';
import { fmtDate } from '../lib/format-time';

interface Subscription {
  id: string;
  product_id: string;
  product_display_name: string;
  product_family: string;
  billing_period: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  seat_count: number;
  /** OCP-569 - billable users in use; null = not yet measurable. */
  seats_in_use?: number | null;
  quota_remaining_metered: number | null;
  allow_overage: boolean;
  paddle_subscription_id: string | null;
  polar_subscription_id: string | null;
  /**
   * OCP-531 - kept in sync with the detail page's shape. This list page draws
   * no lifecycle control, so it never branches on it; if it ever does, this
   * is the field to use - never a single provider's id column.
   */
  billing_provider: 'paddle' | 'polar' | null;
  /** OCP-777 - `ends` = a finite grant (e.g. a subscription code): access ends, nothing renews. */
  renewal?: 'provider' | 'perpetual_free' | 'ends';
  created_at: string;
}

/** Badge classes per status; the label is `status.<key>` in the catalogue (OCP-656). */
const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  trialing: 'bg-sky-100 text-sky-800',
  past_due: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200',
  paused: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  cancelled: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  expired: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
};

export function SubscriptionsPage() {
  const { t } = useTranslation('subscriptions');
  const [items, setItems] = useState<Subscription[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const r = await api<{ items: Subscription[] }>('/v1/portal/subscriptions', {
          signal: ac.signal,
        });
        setItems(r.items);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError)
          setError(t('loadErrorHttp', { status: err.status }));
        else setError(t('loadError'));
      }
    })();
    return () => ac.abort();
  }, [t]);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t('intro')}
        </p>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && !error && <p className="text-sm text-gray-500">{t('loading')}</p>}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {t('empty')}
          <Link to="/products" className="text-brand-700 underline">
            {t('browseProducts')}
          </Link>
          .
        </div>
      )}

      <div className="space-y-3">
        {items?.map((s) => (
          <SubscriptionCard key={s.id} sub={s} />
        ))}
      </div>
    </div>
  );
}

function SubscriptionCard({ sub }: { sub: Subscription }) {
  const { t } = useTranslation('subscriptions');
  const badge = {
    label: STATUS_BADGE[sub.status] ? t(`status.${sub.status}`) : sub.status,
    cls: STATUS_BADGE[sub.status] ?? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  };
  const isCancelled = sub.status === 'cancelled' || sub.status === 'expired';
  return (
    <Link
      to={`/subscriptions/${sub.id}`}
      data-testid={`sub-card-${sub.id}`}
      className={`block rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50/30 ${
        isCancelled ? 'opacity-70' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {sub.product_display_name}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {sub.product_family} · {sub.billing_period}
            {sub.product_family === 'orboto-saas'
              ? sub.seats_in_use != null
                ? t('seatsInUse', { used: sub.seats_in_use, total: sub.seat_count })
                : t('seats', { count: sub.seat_count })
              : sub.seat_count > 1
                ? t('seats', { count: sub.seat_count })
                : ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            data-testid={`sub-status-${sub.id}`}
            className={`rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}
          >
            {badge.label}
          </span>
          {sub.cancel_at_period_end && !isCancelled && (
            <span
              data-testid={`sub-pending-cancel-${sub.id}`}
              className="rounded bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300"
            >
              {t('cancels', { date: formatDate(sub.current_period_end) })}
            </span>
          )}
        </div>
      </div>
      {sub.current_period_end && !isCancelled && (
        <p className="mt-3 text-xs text-gray-500">
          {sub.cancel_at_period_end || sub.renewal === 'ends' ? t('accessUntil') : t('renews')}
          {formatDate(sub.current_period_end)}
        </p>
      )}
    </Link>
  );
}

function formatDate(iso: string | null): string {
  return fmtDate(iso, '—');
}
